/**
 * Where the real-time socket connects — ONE definition, used by both socket clients.
 *
 * It lives in its own module because there are now two clients that must agree: the member
 * client (lib/websocket.ts) and the Platform Console client (lib/platformWebsocket.ts). A second
 * copy of this logic is exactly how one of them ends up pointing at the wrong host in a deployed
 * build — which is the bug the history below records.
 *
 */
// This used to read: "Never use window.location.origin as fallback — that would route WS to
// the web container (port 8081) not the API." That was true, and it is the reason real-time
// has never worked in a deployed build. scripts/pack-tiffin.sh builds the web image with
// `--build-arg VITE_API_URL=` (it MUST stay empty, or the app calls /api/api/v1), so in
// production `_rawUrl` is '' — and the old fallback then pointed every deployed browser at
// `http://localhost:4000`, i.e. at THE END USER'S OWN MACHINE. It failed with nothing but a
// console.warn, which is why it went unnoticed.
//
// The premise has now changed: nginx/nginx.prod.conf has a `/socket.io/` location that proxies
// to the API upstream with WebSocket upgrade handling, exactly as it already does for `/api/`.
// Same-origin is therefore the CORRECT target in a deployed build — the reverse proxy, not the
// web container, decides where /socket.io/ lands.
//
// Dev is the only case that still needs the absolute fallback: `pnpm dev:web` serves from Vite
// on 5173 and does not proxy WebSocket upgrades, so the socket must address the API directly.
// Gated on import.meta.env.DEV so it can never leak into a production bundle again.
const _rawUrl = import.meta.env.VITE_API_URL ?? '';

export const WS_URL = _rawUrl
  ? // Explicit API origin configured (e.g. https://dev.jurivoxa.com) → strip any /api suffix.
    _rawUrl.replace(/\/api\/v1\/?$/, '').replace(/\/api\/?$/, '')
  : import.meta.env.DEV
    ? 'http://localhost:4000'
    : // Production, same-origin: nginx routes /socket.io/ to the API. Using the page's own
      // origin also keeps it correct under whatever hostname the deployment is served on.
      window.location.origin;
